//@ts-strict-ignore
import { graphql } from '@apollo/client/react/hoc';
import { BaseQueryOptions, DocumentNode, OperationVariables, QueryHookOptions, useQuery } from '@apollo/client';
import { Maybe } from '../schema/p4types';

function query(
  query: DocumentNode,
  dataProp?: string,
  variables: Maybe<Function | Object> = undefined,
  fetchPolicy: string = 'cache-and-network',
  { skip }: { skip?: boolean } = { skip: false }
) {
  return graphql(query, {
    options: (props) => {
      let calculatedVariables;
      if (variables && typeof variables === 'function') {
        calculatedVariables = variables(props);
      } else {
        calculatedVariables = variables;
      }
      return {
        variables: calculatedVariables,
        errorPolicy: 'all',
        fetchPolicy: fetchPolicy,
        notifyOnNetworkStatusChange: true,
      } as BaseQueryOptions;
    },
    name: dataProp ? dataProp : 'data',
    skip: skip ? skip : false,
  });
}

/**
 * Query for an Entity.
 * @param query - GraphQL query of the format {query: {[dataProp]: {}}}
 * @param id - id of the requested entity.
 * @param dataProp - (Optional) Specifies the data property in the graphQL provided (e.g. 'productData'). Default is 'data'.
 * @param options - (Optional) Additional/Override options to pass to the query hook.
 * @returns  Either the requested entity, or null.
 */
export const useInstanceQuery = <T>(query: DocumentNode, id: number, { dataProp = 'data', options = {} } = {}) => {
  const { data } = useQuery(query, {
    variables: { id },
    ...options,
  });
  return (data?.[dataProp] as T) || null;
};

/**
 * Query for a Page and map results to a JS List.
 * @param query - GraphQL query
 * @param listProp - (Optional) Specifies the list property in the graphQL provided (e.g. 'listData'). Default is 'list'.
 * @param options - Any additional options - Passed directly through to useQuery.
 * @returns - Either the requested list, or an empty list.
 */
export const useListQuery = <TData = any, TVariables extends OperationVariables = OperationVariables, RowData = any>(
  query: DocumentNode,
  {
    listProp = 'list',
    options = {},
  }: { listProp?: 'list' | string; options?: QueryHookOptions<TData, TVariables> } = {}
) => {
  const { data } = useQuery<TData, TVariables>(query, options);
  return (
    ((data?.[listProp as keyof typeof data] as { edges?: Array<{ node: any }> })?.edges?.map(
      (edge) => edge.node
    ) as Array<RowData>) ?? []
  );
};

export default query;
